import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, CheckCircle2, Clock, UserPlus, Link2, Mail } from "lucide-react";

function SelectionEditor({ school, onSaved }) {
  const [primary, setPrimary] = useState(school.selection?.primary_contact_id || "");
  const [additional, setAdditional] = useState(school.selection?.additional_contact_ids || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPrimary(school.selection?.primary_contact_id || "");
    setAdditional(school.selection?.additional_contact_ids || []);
  }, [school.dir_id, school.selection]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/directory/schools/${school.dir_id}/selection`,
        { primary_contact_id: primary || null, additional_contact_ids: additional });
      toast.success("Contact selection saved. Nothing was emailed — selections only guide coordination.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (school.contacts.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={`selection-editor-${school.dir_id}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-[#78716C]">Coordination contacts</div>
      {school.contacts.map((c) => (
        <div key={c.dir_id} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`contact-row-${c.dir_id}`}>
          <label className="flex items-center gap-1.5">
            <input type="radio" name={`primary-${school.dir_id}`} checked={primary === c.dir_id}
              onChange={() => { setPrimary(c.dir_id); setAdditional(additional.filter((x) => x !== c.dir_id)); }}
              data-testid={`primary-radio-${c.dir_id}`} />
            <span className="text-xs text-[#78716C]">Primary</span></label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={additional.includes(c.dir_id)} disabled={primary === c.dir_id}
              onChange={(e) => setAdditional(e.target.checked ? [...additional, c.dir_id] : additional.filter((x) => x !== c.dir_id))}
              data-testid={`additional-check-${c.dir_id}`} />
            <span className="text-xs text-[#78716C]">Additional</span></label>
          <span className="font-bold text-navy">{c.name}</span>
          <span className="text-xs text-[#78716C]">{c.role}</span>
          <a className="text-xs text-terracotta hover:underline flex items-center gap-1" href={`mailto:${c.email}`}
            data-testid={`contact-email-${c.dir_id}`}><Mail size={11} />{c.email}</a>
        </div>
      ))}
      <button className="btn-outline !py-1.5 text-xs" onClick={save} disabled={busy}
        data-testid={`save-selection-${school.dir_id}`}>{busy ? "Saving…" : "Save selection"}</button>
    </div>
  );
}

function AssignmentEditor({ school, consultants, onSaved }) {
  const [consultantId, setConsultantId] = useState("");
  const active = school.assignments || [];
  const assign = async () => {
    try {
      await api.post("/directory/assignments", { consultant_id: consultantId, directory_school_id: school.dir_id });
      toast.success("Consultant assigned. They can now see this school's designated contacts.");
      setConsultantId("");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  const end = async (a) => {
    if (!window.confirm(`End ${a.consultant_name}'s assignment to ${school.name}? Their access to this school's contacts is revoked immediately; history is preserved.`)) return;
    try { await api.post(`/directory/assignments/${a.id}/end`); onSaved(); toast.success("Assignment ended."); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`assignment-editor-${school.dir_id}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-[#78716C]">Assigned consultants</div>
      {active.length === 0 && <div className="text-xs text-[#78716C]">No consultants assigned.</div>}
      {active.map((a) => (
        <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`dir-assignment-row-${a.id}`}>
          <span className="font-bold text-navy">{a.consultant_name}</span>
          <span className="text-xs text-[#78716C]">from {a.start_date}{a.end_date ? ` to ${a.end_date}` : ""}</span>
          <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" onClick={() => end(a)}
            data-testid={`end-dir-assignment-${a.id}`}>End</button>
        </div>
      ))}
      <div className="flex gap-2">
        <select className="input !py-1.5 text-xs flex-1" value={consultantId}
          onChange={(e) => setConsultantId(e.target.value)} data-testid={`dir-assign-select-${school.dir_id}`}>
          <option value="">Select consultant…</option>
          {consultants.filter((c) => !active.some((a) => a.consultant_id === c.id)).map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.email})</option>))}
        </select>
        <button className="btn-outline !py-1.5 text-xs" onClick={assign} disabled={!consultantId}
          data-testid={`dir-assign-button-${school.dir_id}`}><UserPlus size={13} /> Assign</button>
      </div>
    </div>
  );
}

function LinkEditor({ school, opsSchools, onSaved }) {
  const [choice, setChoice] = useState("");
  const link = school.link || { status: "unmatched", candidates: [] };
  if (link.status === "auto" || link.status === "manual") {
    return <div className="text-xs text-[#57534E]" data-testid={`link-status-${school.dir_id}`}>
      <Link2 size={11} className="inline mr-1" />Linked to Operations school record ({link.status}).</div>;
  }
  return (
    <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-3 py-2 space-y-2" data-testid={`link-review-${school.dir_id}`}>
      <div className="text-xs font-bold text-[#92400E] flex items-center gap-1"><AlertTriangle size={12} />
        {link.status === "ambiguous"
          ? "Multiple Operations schools share this name — review and link manually (no duplicates created)."
          : "No matching Operations school found — left unlinked rather than guessing."}</div>
      <div className="flex gap-2">
        <select className="input !py-1 text-xs flex-1" value={choice} onChange={(e) => setChoice(e.target.value)}
          data-testid={`link-select-${school.dir_id}`}>
          <option value="">Leave unlinked</option>
          {(link.candidates?.length ? link.candidates : opsSchools).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>))}
        </select>
        <button className="btn-ghost !py-1 !px-2 text-xs" data-testid={`link-save-${school.dir_id}`}
          onClick={async () => {
            try { await api.post(`/directory/schools/${school.dir_id}/link`, { ops_school_id: choice || null }); onSaved(); toast.success("Link updated."); }
            catch (e) { toast.error(apiError(e)); }
          }}>Save link</button>
      </div>
    </div>
  );
}

export default function Directory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [schools, setSchools] = useState(null);
  const [mine, setMine] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [opsSchools, setOpsSchools] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    if (isAdmin) {
      api.get("/directory/status").then((r) => setStatus(r.data)).catch(() => {});
      api.get("/directory/schools").then((r) => setSchools(r.data)).catch((e) => { setSchools([]); toast.error(apiError(e)); });
      api.get("/admin/consultants").then((r) => setConsultants(r.data)).catch(() => {});
      api.get("/organizations").then((r) => setOpsSchools(r.data.flatMap((o) => o.schools || []))).catch(() => {});
    } else {
      api.get("/directory/my-contacts").then((r) => setMine(r.data)).catch((e) => { setMine([]); toast.error(apiError(e)); });
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/directory/refresh");
      toast.success(`Directory refreshed (${data.mode}): ${data.schools} schools, ${data.contacts} contacts processed.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setRefreshing(false); }
  };

  if (!isAdmin) {
    if (mine === null) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="directory-loading">Loading directory…</div>;
    return (
      <div className="space-y-6 max-w-3xl" data-testid="directory-consultant-view">
        <div>
          <h1 className="font-serif font-bold text-2xl text-navy">My school contacts</h1>
          <p className="text-sm text-[#57534E]">Designated contacts for schools you're currently assigned to, synced from the CKingEducation website directory. Contact details are managed on the main website.</p>
        </div>
        {mine.length === 0 && (
          <div className="card p-6 text-sm text-[#78716C]" data-testid="directory-empty">
            No active school assignments. Ask an Admin to assign you to a school from the School Directory.</div>)}
        {mine.map((s) => (
          <div key={s.dir_id} className="card p-6 space-y-3" data-testid={`my-school-${s.dir_id}`}>
            <div>
              <div className="font-serif font-bold text-lg text-navy">{s.name}</div>
              {s.district_name && <div className="text-xs text-[#78716C]">{s.district_name}</div>}
            </div>
            {s.contacts.length === 0 && <div className="text-xs text-[#92400E]">No designated contacts are listed for this school yet.</div>}
            {s.contacts.map((c) => (
              <div key={c.dir_id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`my-contact-${c.dir_id}`}>
                <span className="font-bold text-navy">{c.name}</span>
                {c.role && <span className="text-xs text-[#78716C]">{c.role}</span>}
                {c.is_primary && <span className="badge-approved">Primary</span>}
                {c.is_additional && <span className="badge-neutral">Coordination</span>}
                <a className="text-xs text-terracotta hover:underline flex items-center gap-1" href={`mailto:${c.email}`}
                  data-testid={`my-contact-email-${c.dir_id}`}><Mail size={11} />{c.email}</a>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (schools === null) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="directory-loading">Loading directory…</div>;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="directory-admin-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif font-bold text-2xl text-navy">School Directory</h1>
          <p className="text-sm text-[#57534E]">Schools and designated contacts sync from the CKingEducation website (read-only). Operations manages consultant assignments and coordination selections here.</p>
        </div>
        <button className="btn-primary" onClick={refresh} disabled={refreshing || (status && !status.configured)}
          data-testid="directory-refresh-button">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh directory"}</button>
      </div>

      {status && (
        <div className="card p-4 flex flex-wrap items-center gap-4 text-sm" data-testid="directory-status-card">
          {status.stale
            ? <span className="badge-nosend" data-testid="directory-stale-badge"><AlertTriangle size={11} /> Directory may be outdated</span>
            : <span className="badge-approved" data-testid="directory-fresh-badge"><CheckCircle2 size={11} /> Up to date</span>}
          <span className="text-xs text-[#78716C] flex items-center gap-1" data-testid="directory-last-success">
            <Clock size={11} /> Last successful refresh: {status.last_success_at ? new Date(status.last_success_at).toLocaleString() : "never"}</span>
          <span className="text-xs text-[#78716C]">{status.school_count} schools · {status.contact_count} active contacts · auto-refreshes hourly</span>
          {!status.configured && (
            <span className="text-xs font-bold text-[#991B1B]" data-testid="directory-not-configured">
              API key not configured — set CKINGEDUCATION_DIRECTORY_API_KEY in backend secrets.</span>)}
          {status.last_error && <span className="text-xs text-[#991B1B]" data-testid="directory-last-error">Last error: {status.last_error}</span>}
        </div>)}

      {schools.length === 0 && (
        <div className="card p-6 text-sm text-[#78716C]" data-testid="directory-empty-admin">
          No directory data yet. Configure the API key and click "Refresh directory" to run the first full sync.</div>)}

      {schools.map((s) => (
        <div key={s.dir_id} className="card p-6 space-y-4" data-testid={`dir-school-${s.dir_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-serif font-bold text-lg text-navy">{s.name}</span>
            {s.district_name && <span className="text-xs text-[#78716C]">{s.district_name}</span>}
            {!s.active && <span className="badge-nosend">Inactive upstream</span>}
            {s.no_contacts && (
              <span className="badge-nosend" data-testid={`no-contacts-flag-${s.dir_id}`}>
                <AlertTriangle size={11} /> No designated contacts</span>)}
            {s.selection_flags?.map((f, i) => (
              <span key={i} className="badge-draft" data-testid={`selection-flag-${s.dir_id}-${i}`}>
                <AlertTriangle size={11} /> {f}</span>))}
          </div>
          <SelectionEditor school={s} onSaved={load} />
          <AssignmentEditor school={s} consultants={consultants} onSaved={load} />
          <LinkEditor school={s} opsSchools={opsSchools} onSaved={load} />
        </div>
      ))}
    </div>
  );
}
